import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./WhyJoinUs.css";
import Testimonial from "../../../Testimonial/Testimonial";

const WhyJoinUs = () => {
  const techItemsRow1 = [
    { name: "Angular.js", logo: "/logos/angular.png" },
    { name: "HTML", logo: "/logos/html.png" },
    { name: "CSS3", logo: "/logos/css.png" },
    { name: "Bootstrap", logo: "/logos/bootstrap.png" },
    { name: "Javascript", logo: "/logos/javascript.png" },
    { name: "AmazonDB", logo: "/logos/amazon_dynamodb.png" },
    { name: "Appian", logo: "/logos/appian.png" },
    { name: "Laravel", logo: "/logos/laravel.png" },
    { name: "MariaDB", logo: "/logos/mariadb.png" },
    { name: "MongoDB", logo: "/logos/mongodb.png" },
    { name: "Android", logo: "/logos/android-5.png" },
    { name: "ERC-20", logo: "/logos/erc-20.png" },
    { name: "Ethereum", logo: "/logos/ethereum.png" },
    { name: "Flutter", logo: "/logos/flutter.png" },
    { name: "Ionic", logo: "/logos/ionic.png" },
    { name: "Augmented Reality", logo: "/logos/Augmented Reality.png" },
    { name: "Crda", logo: "/logos/crda.png" },
    { name: "Data Science", logo: "/logos/data-science.png" },
    { name: "Go Chain", logo: "/logos/gochain.png" },
  ];

  const techItemsRow2 = [
    { name: "Sass", logo: "/logos/saas.png" },
    { name: "JQuery", logo: "/logos/jquery.png" },
    { name: "Less", logo: "/logos/less.png" },
    { name: "Codeigniter", logo: "/logos/codeignitor.png" },
    { name: "Django", logo: "/logos/django.png" },
    { name: "Node JS", logo: "/logos/node js.png" },
    { name: "PHP", logo: "/logos/php.png" },
    { name: "Salesforce", logo: "/logos/salesforce.png" },
    { name: "SQL Server", logo: "/logos/sql-server.png" },
    { name: "MySQL", logo: "/logos/mysql-5.png" },
    { name: "IOS", logo: "/logos/ios.png" },
    { name: "Oracle 6", logo: "/logos/oracle.png" },
    { name: "PostgreSQL", logo: "/logos/postgresql.png" },
    { name: "React Native", logo: "/logos/react_native.png" },
    { name: "Solidity", logo: "/logos/Solidity.png" },
    { name: "Hyperledger", logo: "/logos/Hyperledger.png" },
    { name: "Internet Of Things", logo: "/logos/iot.png" },
    { name: "One Ledger", logo: "/logos/OneLedger.png" },
    { name: "Virtual Reality", logo: "/logos/virtual-reality.png" },
  ];

  const duplicatedRow1 = [...techItemsRow1, ...techItemsRow1];
  const duplicatedRow2 = [...techItemsRow2, ...techItemsRow2];

  const containerRef = useRef(null);
  const row1Ref = useRef(null);
  const row2Ref = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [offset, setOffset] = useState(0);
  const animationRef = useRef();

  useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }

    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const scrollSpeed = 1;

    const animate = () => {
      setOffset((prevOffset) => {
        const maxOffset = row1Ref.current?.scrollWidth / 2 || 0;
        const newOffset = prevOffset + scrollSpeed;
        return newOffset >= maxOffset ? 0 : newOffset;
      });
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [containerWidth]);

  return (
    <div>
      {/* ✅ Helmet Meta Tags */}
      <Helmet>
        <title>Why Join Us | Careers, Growth & Technology Opportunities</title>
        <meta
          name="description"
          content="Discover why you should join us. Explore exciting opportunities in Artificial Intelligence, Blockchain, Cloud, Data Science, IoT, AR/VR, and more. Build your career with cutting-edge technologies and an innovative team."
        />
        <meta
          name="keywords"
          content="Why Join Us, Careers in Technology, AI Jobs, Blockchain Careers, Data Science Opportunities, Cloud Computing Jobs, IT Career Growth, Web Development Jobs, Angular Careers, React Native Jobs, Node.js Jobs, PHP Careers, SQL Server Jobs, MongoDB Careers, Ethereum Blockchain Jobs, Hyperledger Careers, IoT Jobs, Artificial Intelligence Careers, Augmented Reality Jobs, Virtual Reality Careers, Flutter Jobs, Software Development Careers, Future Technology Jobs"
        />
      </Helmet>

      <div className="mke-why-join-container">
        <section className="mke-hero-section">
          <div className="mke-hero-content">
            <h1 className="mke-hero-title">
              <span className="mke-title-part">Why Join</span>
              <span className="mke-title-accent">MK E-Tech?</span>
            </h1>
            <p className="mke-hero-subtitle">
              Shape the future of technology with innovators who are passionate
              about creating transformative software solutions.
            </p>
          </div>
          <div className="mke-hero-animation">
            <div className="mke-animated-shape mke-shape-1"></div>
            <div className="mke-animated-shape mke-shape-2"></div>
            <div className="mke-animated-shape mke-shape-3"></div>
          </div>
        </section>

        <section className="mke-benefits-section">
          <div className="mke-section-header">
            <h2 className="mke-section-title">Our Benefits</h2>
            <p className="mke-section-description">
              We invest in our team's growth and well-being with
              industry-leading benefits
            </p>
          </div>

          <div className="mke-benefits-grid">
            <div className="mke-benefit-card mke-benefit-card--1">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Career Growth</h3>
              <p className="mke-benefit-description">
                Structured mentorship programs, skill development workshops, and
                clear paths for advancement within the company.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--2">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Innovative Projects</h3>
              <p className="mke-benefit-description">
                Work with cutting-edge technologies on projects that challenge
                the status quo and make a real impact.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--3">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17.66,8L12,2.35L6.34,8C4.78,9.56 4,11.64 4,13.64C4,15.64 4.78,17.75 6.34,19.31C7.9,20.87 9.95,21.66 12,21.66C14.05,21.66 16.1,20.87 17.66,19.31C19.22,17.75 20,15.64 20,13.64C20,11.64 19.22,9.56 17.66,8M6,14C6,12 6.62,10.73 7.76,9.6L12,5.27L16.24,9.65C17.38,10.77 18,12 18,14H6Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Work-Life Balance</h3>
              <p className="mke-benefit-description">
                Flexible scheduling, remote work options, and generous time-off
                policies to support your well-being.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--4">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Competitive Compensation</h3>
              <p className="mke-benefit-description">
                Attractive salary packages, performance bonuses, and
                comprehensive health benefits for you and your family.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--5">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Learning & Development</h3>
              <p className="mke-benefit-description">
                Annual training budget, certification support, and access to
                premium learning platforms to fuel your professional growth.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--6">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Collaborative Environment</h3>
              <p className="mke-benefit-description">
                Work with brilliant minds in an inclusive culture that values
                diversity, teamwork, and knowledge sharing.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--7">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17,13H13V17H11V13H7V11H11V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Wellness Programs</h3>
              <p className="mke-benefit-description">
                Comprehensive wellness initiatives including gym memberships,
                mental health support, yoga classes, and wellness challenges.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--8">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,3L2,12H5V20H19V12H22L12,3M12,7.7L16.2,12H15V18H9V12H7.8L12,7.7Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Modern Workspaces</h3>
              <p className="mke-benefit-description">
                State-of-the-art offices with ergonomic furniture, recreational
                areas, and cutting-edge technology infrastructure.
              </p>
            </div>

            {/* Additional Benefit Cards */}
            <div className="mke-benefit-card mke-benefit-card--9">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17,18A2,2 0 0,1 19,20A2,2 0 0,1 17,22C15.89,22 15,21.1 15,20A2,2 0 0,1 17,18M1,2H4.27L5.21,4H20A1,1 0 0,1 21,5C21,5.17 20.95,5.34 20.88,5.5L17.3,11.97C16.96,12.58 16.3,13 15.55,13H8.1L7.2,14.63L7.17,14.75A0.25,0.25 0 0,0 7.42,15H19V17H7C5.89,17 5,16.1 5,15C5,14.65 5.09,14.32 5.24,14L6.6,11.59L3,4H1V2M7,18A2,2 0 0,1 9,20A2,2 0 0,1 7,22C5.89,22 5,21.1 5,20A2,2 0 0,1 7,18M16,11L18.78,6H6.14L8.5,11H16Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Stock Options</h3>
              <p className="mke-benefit-description">
                Equity participation programs that allow you to share in the
                company's success and growth as we expand.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--10">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 极速 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 极速 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,极速 14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Global Opportunities</h3>
              <p className="mke-benefit-description">
                International assignments, client projects, and transfer
                opportunities across our global offices in 12+ countries.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--11">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 极速 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6M12,8A4,4 0 0,1 16,12A4,4 0 0,1 12,16A4,4 0 0,1 8,12A4,4 0 0,1 12,8Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Cutting-Edge Tools</h3>
              <p className="mke-benefit-description">
                Access to the latest software, hardware, and development tools
                to ensure you have everything needed to excel in your role.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--12">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Community Impact</h3>
              <p className="mke-benefit-description">
                Paid volunteer time, company-sponsored charity events, and
                opportunities to work on projects that make a positive social
                impact.
              </p>
            </div>
            <div className="mke-benefit-card mke-benefit-card--13">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 极速 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,极速 16.18 8.41,14.5 12,14.5C15.59,14.5 极速 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Flexible Work Arrangements</h3>
              <p className="mke-benefit-description">
                Customizable work schedules, hybrid models, and remote work
                options to fit your lifestyle and maximize productivity.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--14">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 极速 4,12A8,8 极速 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,极速 12A6,6 0 0,1 12,6M12,8A4,4 0 0,0 8,12A4,4 0 0,极速 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Retirement Planning</h3>
              <p className="mke-benefit-description">
                401(k) matching, financial planning services, and retirement
                counseling to help you secure your financial future.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--15">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Parental Support</h3>
              <p className="mke-benefit-description">
                Generous parental leave policies, childcare assistance, and
                flexible return-to-work programs for new parents.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--16">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17,13H13V17H11V极速 13H7V11H11V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Recognition Programs</h3>
              <p className="mke-benefit-description">
                Regular employee recognition, performance awards, and
                peer-to-peer appreciation programs that celebrate your
                achievements.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--17">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,3L2,12H5V20H19V12H22L12,3M12,7.7L16.2,12H15V18H9V12H7.8L12,7.7Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Technology Allowance</h3>
              <p className="mke-benefit-description">
                Annual technology stipend for home office setup, latest devices,
                and tools to enhance your productivity.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--18">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 极速 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.极速 07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27极速 C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Leadership Development</h3>
              <p className="mke-benefit-description">
                Specialized programs for emerging leaders, executive coaching,
                and opportunities to lead cross-functional initiatives.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--19">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Health & Wellness</h3>
              <p className="mke-benefit-description">
                Comprehensive medical, dental, and vision insurance, plus
                wellness programs, fitness subsidies, and mental health support.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--20">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17.66,8L12,2.35L6.34,8C4.78,9.56 4,11.64 4,13.64C4,15.64 4.78,17.75 6.34,19.31C7.9,20.87 9.95,21.66 12,21.66C14.05,21.66 16.1,20.87 17.66,极速 19.31C19.22,17.75 20,15.极速 64 20,13.64C20,11.64 19.22,9.56 17.66,8M6,14C6,12 6.62,10.73 7.76,9.6L12,5.27L16.24,9.65C17.38,10.77 18,12 18,14H6Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Company Events</h3>
              <p className="mke-benefit-description">
                Regular team-building activities, holiday parties, hackathons,
                and social events that foster community and fun.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--21">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 极速 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,极速 0 12,8Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Travel Opportunities</h3>
              <p className="mke-benefit-description">
                Domestic and international business travel, client site visits,
                and opportunities to attend global conferences and industry
                events.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--22">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,极速 12L11,16.5Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Professional Certifications</h3>
              <p className="mke-benefit-description">
                Company-sponsored certification programs, exam fee coverage, and
                dedicated study time for professional development.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--23">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17,13H13V17H11V13H7V11H11V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Employee Referral Program</h3>
              <p className="mke-benefit-description">
                Generous bonuses for referring talented colleagues, with
                additional rewards for successful long-term placements.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--24">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,3L2,12H5V20H19V12H22L12,3M12,7.7L16.2,12H15V18H9V12H7.8L12,7.7Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Research & Innovation Time</h3>
              <p className="mke-benefit-description">
                Dedicated time for personal projects, research, and innovation
                that can benefit the company and advance your skills.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--25">
              <div className="mke-benefit-icon">
                <svg className="极速 mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73极速 L19.66,5.27C19.54,5.05 19.27,4.96 极速 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.极速 54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Mentorship Program</h3>
              <p className="mke-benefit-description">
                Structured mentorship matching with senior leaders, regular
                guidance sessions, and career development planning.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--26">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M21,7L9,19极速 L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Continuing Education</h3>
              <p className="mke-benefit-description">
                Tuition reimbursement for advanced degrees, professional
                courses, and ongoing education to support your career growth.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--27">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M17.66,8L12,2.35L6.34,8C4.78,9.56 4,11.64 4,13.64C4,15.64 4.78,17.75 6.34,19.31C7.9,20.87 9.95,21.66 12,21.66C14.05,21.66 16.1,20.87 17.66,19.31C19.22,17.75 20,15.64 20,13.64C20,11.64 19.22,9.56 17.66,8M6,14C6,12 6.62,10.73 7.76,9.6L12,5.27L16.24,9.65C17.38,10.77 18,12 18,14H6Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Health Savings Account</h3>
              <p className="mke-benefit-description">
                Company contributions to HSAs, flexible spending accounts, and
                comprehensive health financial planning support.
              </p>
            </div>

            <div className="mke-benefit-card mke-benefit-card--28">
              <div className="mke-benefit-icon">
                <svg className="mke-icon-svg" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.极速 5,9A3.5,3.5 0 0,1 极速 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38极速 C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,极速 16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V极速 18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.极速 94 24,17.11 24,18.5V20Z"
                  />
                </svg>
              </div>
              <h3 className="mke-benefit-title">Diversity & Inclusion</h3>
              <p className="mke-benefit-description">
                Active employee resource groups, diversity training, and
                inclusive policies that celebrate our diverse workforce.
              </p>
            </div>
          </div>
        </section>

        <section className="mke-culture-section">
          <div className="mke-culture-content">
            <div className="mke-culture-text">
              <h2 className="mke-culture-title">Our Culture</h2>
              <p className="mke-culture-description">
                At MK E-Tech, we foster an environment of collaboration,
                innovation, and continuous learning. Our team members are
                encouraged to think creatively, take calculated risks, and bring
                their authentic selves to work every day.
              </p>
              <ul className="mke-culture-list">
                <li className="mke-culture-item">
                  <span className="mke-culture-highlight">
                    Open Communication:
                  </span>{" "}
                  Transparent dialogue across all levels of the organization
                </li>
                <li className="mke-culture-item">
                  <span className="mke-culture-highlight">
                    Collaborative Spirit:
                  </span>{" "}
                  Team-based approach to problem-solving and innovation
                </li>
                <li className="mke-culture-item">
                  <span className="mke-culture-highlight">
                    Continuous Learning:
                  </span>{" "}
                  Regular knowledge-sharing sessions and learning opportunities
                </li>
                <li className="mke-culture-item">
                  <span className="mke-culture-highlight">
                    Innovation Focus:
                  </span>{" "}
                  Encouraging creative thinking and experimentation with new
                  ideas
                </li>
                <li className="mke-culture-item">
                  <span className="mke-culture-highlight">
                    Work-Life Integration:
                  </span>{" "}
                  Supporting flexibility and balance in how and where work gets
                  done
                </li>
                <li className="mke-culture-item">
                  <span className="mke-culture-highlight">
                    Diversity & Inclusion:
                  </span>{" "}
                  Celebrating diverse perspectives and creating an inclusive
                  environment
                </li>
                <li className="mke-culture-item">
                  <span className="mke-culture-highlight">
                    Ownership Mentality:
                  </span>{" "}
                  Empowering employees to take ownership of projects and
                  outcomes
                </li>
                <li className="mke-culture-item">
                  <span className="mke-culture-highlight">
                    Customer-Centric Approach:
                  </span>{" "}
                  Keeping customer needs at the center of everything we do
                </li>
              </ul>
            </div>
            <div className="mke-culture-visual">
              <div className="mke-culture-animation">
                <div className="mke-culture-dot mke-culture-dot--1"></div>
                <div className="mke-culture-dot mke-culture-dot--2"></div>
                <div className="mke-culture-dot mke-culture-dot--3"></div>
                <div className="mke-culture-dot mke-culture-dot--4"></div>
                <div className="mke-culture-dot mke-culture-dot--5"></div>
                <div className="mke-culture-dot mke-culture-dot--6"></div>
                <div className="mke-culture-dot mke-culture-dot--7"></div>
                <div className="mke-culture-dot mke-culture-dot--8"></div>
              </div>
            </div>
          </div>

          {/* Culture Values Grid */}
          <div className="mke-culture-values">
  <h3 className="mke-culture-values-title">Our Core Values</h3>
  <div className="mke-culture-values-grid">
    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,2A10,10 0 0,1 22,12A10,10 极速 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67极速 L7.91,10.59L6.5,12L11,16.5Z" />
        </svg>
      </div>
      <h4>Excellence</h4>
      <p>Striving for the highest quality in everything we do</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,4A4,4 0 0,1 16,极速 8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
        </svg>
      </div>
      <h4>Collaboration</h4>
      <p>Working together to achieve extraordinary results</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="极速 0 0 24 24" fill="currentColor">
          <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
        </svg>
      </div>
      <h4>Innovation</h4>
      <p>Embracing new ideas and creative solutions</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,极速 1 12,22A10,10 0 0,1 2,极速 12A10,10 0 0,1 12,2M13,17H11V15H13V17M13,13H11极速 V7H13V13Z" />
        </svg>
      </div>
      <h4>Integrity</h4>
      <p>Doing the right thing, even when no one is watching</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17,13H13V17H11V13H7V11H11V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
        </svg>
      </div>
      <h4>Accountability</h4>
      <p>Taking ownership of our actions and delivering on commitments</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,极速 0 20,12A8,8 0 0,0 12,4M12,6A6,6 极速 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8Z" />
        </svg>
      </div>
      <h4>Customer Focus</h4>
      <p>Putting our clients' needs at the heart of every decision</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 极速 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,极速 1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25V20H0M24,20H20.5V18.25C20.5,17.22 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z" />
        </svg>
      </div>
      <h4>Diversity & Inclusion</h4>
      <p>Valuing diverse perspectives and creating an inclusive environment</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,3L2,12H5V20H19V12H22L12,3M12,7.7L16.2,12H15V18H9V12H7.8L12,极速 7.7Z" />
        </svg>
      </div>
      <h4>Continuous Learning</h4>
      <p>Constantly growing our skills and knowledge to stay ahead</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.66,8L12,2.35L6.34,8C4.78,9.56 4,11.64 4,13.64C4,15.64 4.78,17.75 6.34,19.31C7.极速 9,20.87 9.95,21.66 12,21.66C14.05,21.66 16.1,20.87 17.66,19.31C19.22,17.75 20,15.64 20,13.64C20,11.64 19.22,9.56 17.66,8M6,14C6,12 6.62,10.73 7.76,9.6L12,5.27L16.24,9.65C17.38,10.77 18,12 18,14H6Z" />
        </svg>
      </div>
      <h4>Agility</h4>
      <p>Adapting quickly to change and embracing new challenges</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.极速 5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 极速 0 0,极速 1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L极速 16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.极速 46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94极速 C7.96,18.34 8.5,18.极速 68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
        </svg>
      </div>
      <h4>Passion</h4>
      <p>Bringing energy and enthusiasm to everything we undertake</p>
    </div>

     <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 极速 0,1 12,6M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8Z" />
        </svg>
      </div>
      <h4>Transparency</h4>
      <p>Open communication and honest dialogue at all levels of the organization</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17,13H13V17H11V13H7V11H11极速 V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
        </svg>
      </div>
      <h4>Respect</h4>
      <p>Valuing every individual and treating everyone with dignity and consideration</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,3L2,12H5V20H19V12H22L12,3M12,7.7L16.2,12H15V18H9V12H7.8L12,7.7Z" />
        </svg>
      </div>
      <h4>Ownership</h4>
      <p>Taking responsibility for outcomes and driving initiatives to completion</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
        </svg>
      </div>
      <h4>Quality</h4>
      <p>Delivering exceptional work that exceeds expectations and standards</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,极速 12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42极速 C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.极速 13,5.07C8.5,5.32 7.96,5.66 7.44,极速 6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.21,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.21,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,极速 21.82 14.5,21.58L14.87,18.93C15.5,18.67 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z" />
        </svg>
      </div>
      <h4>Empowerment</h4>
      <p>Enabling team members to make decisions and take initiative</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.66,8L12,2.35L6.34,8极速 C4.78,9.56 4,11.64 4,13.64C4,15.64 4.78,17.75 6.34,19.31C7.9,20.87 9.95,21.66 12,21.66C14.05,21.66 16.1,20.87 17.66,19.31C19.22,17.75 20,15.64 20,13.64C20,11.64 19.22,9.56 17.66,8M6,14C6,12 6.62,10.73 7.76,极速 9.6L12,5.27L16.24,9.65C17.38,10.77 18,12 18,14H6Z" />
        </svg>
      </div>
      <h4>Adaptability</h4>
      <p>Thriving in changing environments and embracing new approaches</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg view极速 Box="0 0 24 24" fill="currentColor">
          <path d="M12,5.5A3.5,3.5 0 0,1 15.5,9A3.5,3.5 0 0,1 12,12.5A3.极速 5,3.5 0 0,1 8.5,9A3.5,3.5 0 0,1 12,5.5M5,8C5.56,8 6.08,8.15 6.53,8.42C6.38,9.85 6.8,11.27 7.66,12.38C7.16,13.34 6.16,14 5,14A3,3 0 0,1 2,11A3,3 0 0,1 5,8M19,8A3,3 0 0,1 22,11A3,3 0 0,1 19,14C17.84,14 16.84,13.34 16.34,12.38C17.2,11.27 17.62,9.85 17.47,8.42C17.92,8.15 18.44,8 19,8M5.5,18.25C5.5,16.18 8.41,14.5 12,14.5C15.59,14.5 18.5,16.18 18.5,18.25V20H5.5V18.25M0,20V18.5C0,17.11 1.89,15.94 4.45,15.6C3.86,16.28 3.5,17.22 3.5,18.25极速 V20H0M24,20H20.5V18.25C20.5,17.22 极速 20.14,16.28 19.55,15.6C22.11,15.94 24,17.11 24,18.5V20Z" />
        </svg>
      </div>
      <h4>Teamwork</h4>
      <p>Collaborating effectively to achieve shared goals and success</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z" />
        </svg>
      </div>
      <h4>Reliability</h4>
      <p>Consistently delivering on promises and maintaining trust</p>
    </div>
        <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,极速 0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,6A6,6 0 0,1 极速 18,12A6,6 0 0,1 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6M12,8A4,4 0 0,0 8,12A4,4 0 0,0 12,16A4,4 0 0,0 16,12A4,4 0 0,0 12,8Z" />
        </svg>
      </div>
      <h4>Transparency</h4>
      <p>Open communication and honest dialogue at all levels of the organization</p>
    </div>

    <div className="mke-culture-value-card">
      <div className="mke-culture-value-icon">
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17,13H13V17H11V13H7V11H11V7H13V11H17M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
        </svg>
      </div>
      <h4>Respect</h4>
      <p>Valuing every individual and treating everyone with dignity and consideration</p>
    </div>
  </div>
</div>
        </section>

        <section className="mke-cta-section">
          <div className="mke-cta-content">
            <h2 className="mke-cta-title">Ready to Join Our Team?</h2>
            <p className="mke-cta-text">
              Explore our current openings and discover where you fit in our
              growing team of innovators and problem-solvers.
            </p>
            <a
              href="https://wa.me/919155599968?text=Hi!%20I%20am%20interested%20in%20viewing%20the%20current%20open%20positions%20and%20career%20opportunities%20at%20your%20organization.%20Please%20share%20the%20details."
              target="_blank"
              rel="noreferrer"
            >
              <button className="mke-cta-button">
                Get in Touch
                <span className="mke-cta-button-arrow"></span>
              </button>
            </a>
          </div>
        </section>
      </div>

      <Testimonial />

      <div className="tech-slider-container" ref={containerRef}>
        <h2 className="tech-slider-title">Tools & Tech Stack</h2>
        <div className="tech-slider-divider"></div>

        {/* First row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row1Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow1.map((tech, index) => (
              <div key={`row1-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Second row */}
        <div className="tech-slider-wrapper">
          <div
            className="tech-slider-row"
            ref={row2Ref}
            style={{ transform: `translateX(-${offset}px)` }}
          >
            {duplicatedRow2.map((tech, index) => (
              <div key={`row2-${index}`} className="tech-slide">
                <img
                  src={tech.logo}
                  alt={tech.name}
                  className="tech-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/logos/default.png";
                  }}
                />
                <span className="tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyJoinUs;
